import type { ErrorEvent, EventHint } from '@sentry/core'

const MAX_BODY_CHARS = 4000

function safeSerialize(value: unknown, maxLen: number): string {
  try {
    if (typeof value === 'string') {
      return value.length > maxLen ? `${value.slice(0, maxLen)}…` : value
    }
    const s = JSON.stringify(value)
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s
  } catch {
    return '[unserializable]'
  }
}

function pickResponseHeaders(headers?: Headers): Record<string, string> {
  if (!headers || typeof headers.get !== 'function') return {}
  const out: Record<string, string> = {}
  const keys = [
    'content-type',
    'x-request-id',
    'x-correlation-id',
    'x-trace-id',
    'cf-ray',
  ] as const
  for (const k of keys) {
    const v = headers.get(k)
    if (v) out[k] = v
  }
  return out
}

/** oazapfts `HttpError`: message `Error: <status>`, numeric `status`, optional `data` / `headers`. */
export function isLikelyOazapftsHttpError(
  exception: unknown
): exception is Error & { status: number; data: unknown; headers?: Headers } {
  if (!exception || typeof exception !== 'object') return false
  if (!(exception instanceof Error)) return false
  const e = exception as Error & { status?: unknown; data?: unknown }
  if (typeof e.status !== 'number' || e.status < 400 || e.status > 599) {
    return false
  }
  return /^Error: \d{3}$/.test(e.message)
}

export function applyHttpErrorContextToEvent(
  event: ErrorEvent,
  hint: EventHint
): void {
  const ex = hint.originalException
  if (!isLikelyOazapftsHttpError(ex)) return

  event.extra = {
    ...event.extra,
    http_error_status: ex.status,
    http_error_body: safeSerialize(ex.data, MAX_BODY_CHARS),
  }

  const hdrs = pickResponseHeaders(ex.headers)
  if (Object.keys(hdrs).length > 0) {
    event.extra.http_response_headers = hdrs
  }

  const finger = ['oazapfts-http', String(ex.status)]
  if (ex.data && typeof ex.data === 'object' && 'code' in ex.data) {
    const code = (ex.data as { code?: unknown }).code
    if (typeof code === 'string' || typeof code === 'number') {
      finger.push(String(code))
    }
  }
  event.fingerprint = finger
}

export function sentryBeforeSend(
  event: ErrorEvent,
  hint: EventHint
): ErrorEvent | null {
  applyHttpErrorContextToEvent(event, hint)
  return event
}
